import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import * as documentService from '../../services/documents';
import * as categoryService from '../../services/categories';
import * as folderService from '../../services/folders';
import '../../styles/main.scss';

const CreateDocument = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    folderId: '',
    type: 'other',
    priority: 'medium',
    status: 'draft',
    dueDate: '',
    tags: '',
  });

  const [attachments, setAttachments] = useState([]);

  const { data: categoriesResponse, isLoading: isCategoriesLoading } = useQuery(
    ['categories', { page: 1, limit: 100 }],
    () => categoryService.getCategories({ page: 1, limit: 100 })
  );

  const { data: foldersResponse, isLoading: isFoldersLoading } = useQuery(
    ['folders', formData.categoryId],
    () =>
      formData.categoryId
        ? folderService.getFolders({ categoryId: formData.categoryId })
        : Promise.resolve({ folders: [] }),
    {
      enabled: !!formData.categoryId,
    }
  );

  const categories =
    categoriesResponse?.data?.data ||
    categoriesResponse?.data ||
    categoriesResponse?.categories ||
    [];

  const folders =
    foldersResponse?.data?.data ||
    foldersResponse?.data ||
    foldersResponse?.folders ||
    [];

  const createDocumentMutation = useMutation(
    (payload) => documentService.createDocument(payload),
    {
      onSuccess: (response) => {
        toast.success('Document created successfully');
        const createdId = response?.data?._id || response?.document?._id || response?._id;
        if (createdId) {
          navigate(`/documents/${createdId}`);
        } else {
          navigate('/documents');
        }
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to create document');
      },
    }
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAttachmentsChange = (event) => {
    const files = Array.from(event.target.files || []);
    setAttachments(files);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.name || !formData.categoryId) {
      toast.error('Name and category are required');
      return;
    }

    const payload = {
      ...formData,
      tags: formData.tags
        ? formData.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : [],
      attachments,
    };

    createDocumentMutation.mutate(payload);
  };

  return (
    <div className="dashboard-page create-document-page">
      <div className="page-header">
        <div>
          <h1>Create Document</h1>
          <p>Create a new document and upload related files</p>
        </div>
      </div>

      <div className="page-content">
        <motion.div
          className="section-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <form onSubmit={handleSubmit} className="form-grid">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(event) => handleChange('name', event.target.value)}
                placeholder="Enter document name"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={formData.description}
                onChange={(event) => handleChange('description', event.target.value)}
                placeholder="Short description of the document"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select
                  className="form-control"
                  value={formData.categoryId}
                  onChange={(event) =>
                    handleChange('categoryId', event.target.value)
                  }
                  disabled={isCategoriesLoading}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Folder</label>
                <select
                  className="form-control"
                  value={formData.folderId}
                  onChange={(event) =>
                    handleChange('folderId', event.target.value)
                  }
                  disabled={!formData.categoryId || isFoldersLoading}
                >
                  <option value="">Select folder</option>
                  {folders.map((folder) => (
                    <option key={folder._id} value={folder._id}>
                      {folder.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <select
                  className="form-control"
                  value={formData.type}
                  onChange={(event) => handleChange('type', event.target.value)}
                >
                  <option value="invoice">Invoice</option>
                  <option value="contract">Contract</option>
                  <option value="report">Report</option>
                  <option value="letter">Letter</option>
                  <option value="memo">Memo</option>
                  <option value="proposal">Proposal</option>
                  <option value="form">Form</option>
                  <option value="certificate">Certificate</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select
                  className="form-control"
                  value={formData.priority}
                  onChange={(event) =>
                    handleChange('priority', event.target.value)
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-control"
                  value={formData.status}
                  onChange={(event) =>
                    handleChange('status', event.target.value)
                  }
                >
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Due date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.dueDate}
                  onChange={(event) =>
                    handleChange('dueDate', event.target.value)
                  }
                />
              </div>

              <div className="form-group">
                <label>Tags</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.tags}
                  onChange={(event) => handleChange('tags', event.target.value)}
                  placeholder="Comma-separated tags"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Attachments</label>
              <input
                type="file"
                className="form-control"
                multiple
                onChange={handleAttachmentsChange}
              />
              {attachments.length > 0 && (
                <div className="text-sm text-muted" style={{ marginTop: '0.5rem' }}>
                  {attachments.length} file(s) selected
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createDocumentMutation.isLoading}
              >
                {createDocumentMutation.isLoading ? 'Creating...' : 'Create document'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateDocument;
